import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import TopNav from "@/components/TopNav";
import JobsDock from "@/components/JobsDock";
import ErrorBoundary from "@/components/ErrorBoundary";
import Lightbox from "@/components/Lightbox";
import HistoryHeader from "@/components/HistoryHeader";
import RecycleBin from "@/components/RecycleBin";
import { api, errMsg, brand } from "@/lib/api";
import { clearSlots } from "@/lib/useUploader";
import { useListNav } from "@/components/NavArrows";

const TOOL_SLOTS = { "bfx-clean-plate": ["cp-source", "cp-plates", "cp-mask"], "bfx-action": ["act-source", "act-plates", "act-mask"], "bfx-cg-render": ["cg-source", "cg-mask"] };
import ImageStudio from "@/sections/ImageStudio";
import VideoStudio from "@/sections/VideoStudio";
import RemixStudio from "@/sections/RemixStudio";
import AudioStudio from "@/sections/AudioStudio";
import BlackFXStudio from "@/sections/BlackFXStudio";

const SECTIONS = { image: ImageStudio, video: VideoStudio, remix: RemixStudio, audio: AudioStudio, blackfx: BlackFXStudio };

export default function Studio() {
  const { projectId, section } = useParams();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState({ models: [], configured: true, error: null });
  const [history, setHistory] = useState([]);
  const [references, setReferences] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [seed, setSeed] = useState(null);
  const [likedOnly, setLikedOnly] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [resetN, setResetN] = useState(0);
  const [params, setParams] = useSearchParams();
  const recreateId = params.get("recreate");
  const handled = React.useRef(null);
  useEffect(() => {
    if (!recreateId || handled.current === recreateId) return;
    handled.current = recreateId;
    const tid = toast.loading("Loading the settings and attaching the result as reference…");
    api.post(`/public/feed/${recreateId}/recreate`, { project_id: projectId })
      .then(({ data }) => { setReferences((r) => [data.reference, ...r]); setTimeout(() => setSeed({ ...data.seed, _t: Date.now() }), 50); toast.success("Ready — prompt, settings and reference are filled in", { id: tid }); })
      .catch((e) => toast.error(errMsg(e, "Could not recreate this post"), { id: tid }))
      .finally(() => setParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recreateId]);
  const loc = useLocation();
  const pickId = params.get("model");
  const modelPick = useMemo(() => (pickId ? { id: pickId, _t: loc.key } : null), [pickId, loc.key]);

  useEffect(() => { sessionStorage.setItem("ax_last_studio", `/studio/${projectId}/${section}`); }, [projectId, section]);
  useEffect(() => { api.get("/models").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  const loadHistory = useCallback(() => {
    api.get(`/generations?project_id=${projectId}`).then((r) => setHistory(r.data.generations || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => {
    loadHistory();
    api.get(`/references?project_id=${projectId}`).then((r) => setReferences(r.data.references || r.data || [])).catch(() => {});
  }, [projectId, loadHistory]);

  const running = history.some((g) => g.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(loadHistory, 4000);
    return () => clearInterval(t);
  }, [running, loadHistory]);

  const generate = useCallback(async (payload, onError) => {
    try {
      const { data } = await api.post("/generations", { project_id: projectId, ...payload });
      setHistory((h) => [data, ...h]);
      toast.success("Request sent — your result will appear in History");
      return true;
    } catch (e) { const m = errMsg(e, "Could not start generation"); if (onError) onError(m); else toast.error(m); return false; }
  }, [projectId]);

  const onUploaded = useCallback((ref) => setReferences((r) => [ref, ...r]), []);
  // Recreate: make sure every media the generation used is loaded, then seed the section with it.
  const recreate = useCallback(async (g) => {
    const bfxTool = g.settings?.bfx_tool;
    if (["bfx-clean-plate", "bfx-cg-render", "bfx-action"].includes(bfxTool)) {
      window.dispatchEvent(new Event("ax-cp-reset"));
      clearSlots(projectId, TOOL_SLOTS[bfxTool]);
      const tid = toast.loading("Restoring the session used for this result…");
      try {
        const { data } = await api.post(`/generations/${g.id}/restore-session`);
        try { localStorage.setItem(`ax_bfx_tool_${projectId}`, bfxTool); } catch { /* storage blocked */ }
        setSeed(null);
        setResetN((n) => n + 1);
        toast.success(data.full ? "Recreate — source, keyframes, plates, prompts and settings are restored" : "Recreate — source, media and settings restored (older item: re-mark keyframes if needed)", { id: tid });
      } catch (e) { toast.error(errMsg(e, "Could not restore this session"), { id: tid }); setResetN((n) => n + 1); }
      return;
    }
    const ids = [...(g.image_ids || []), g.video_id, g.audio_id, g.end_image_id, g.reference_id, g.settings?.mask_id].filter(Boolean);
    const missing = ids.filter((id) => !references.some((r) => r.id === id));
    if (missing.length) {
      try {
        const { data } = await api.get(`/references/by-ids?ids=${missing.join(",")}`);
        if (data.references?.length) setReferences((r) => [...data.references, ...r]);
      } catch { /* attach what we have */ }
    }
    setTimeout(() => setSeed({ ...g, _t: Date.now() }), 50);
    toast.success("Recreate — prompt, settings and media are filled in");
  }, [references, projectId]);
  const items = useMemo(() => history.filter((g) => (g.section || g.category) === section && (!likedOnly || g.liked)), [history, section, likedOnly]);
  const lbNav = useListNav(items.filter((g) => (g.output_urls || []).length), lightbox, setLightbox);
  const onReset = useCallback(async () => {
    const pre = `ax_draft_${projectId}_${section === "blackfx" ? "bfx" : section}`;
    try { Object.keys(localStorage).filter((k) => k.startsWith(pre)).forEach((k) => localStorage.removeItem(k)); } catch { /* storage blocked */ }
    const curTool = section === "blackfx" ? localStorage.getItem(`ax_bfx_tool_${projectId}`) : null;
    if (TOOL_SLOTS[curTool]) clearSlots(projectId, TOOL_SLOTS[curTool]);
    if (section === "blackfx" && localStorage.getItem(`ax_bfx_tool_${projectId}`) === "bfx-cg-render") {
      window.dispatchEvent(new Event("ax-cp-reset"));
      await api.put("/bfx/cleanplate/session", { project_id: projectId, state: {}, tool: "cgrender" }).catch(() => {});
    }
    const cpTool = { "bfx-clean-plate": "cleanplate", "bfx-action": "action" }[section === "blackfx" ? localStorage.getItem(`ax_bfx_tool_${projectId}`) : ""];
    if (cpTool) {
      window.dispatchEvent(new Event("ax-cp-reset"));
      await api.put("/bfx/cleanplate/session", { project_id: projectId, state: {}, tool: cpTool }).catch(() => {});
      await api.put("/bfx/cleanplate/session/mask", { project_id: projectId, mask: null, alpha_job: null, tool: cpTool }).catch(() => {});
    }
    setSeed(null);
    setResetN((n) => n + 1);
    toast.success("Panel cleared — start fresh");
  }, [projectId, section]);
  const doneKey = history.filter((g) => g.status === "succeeded").length;
  const header = (
    <HistoryHeader projectId={projectId} count={items.length} unit={section === "image" ? "images" : section === "audio" || section === "blackfx" ? "items" : "videos"}
      refreshKey={`${doneKey}-${tick}`} likedOnly={likedOnly} setLikedOnly={setLikedOnly} onOpenTrash={() => setTrashOpen(true)}
      onReset={onReset} items={items} canStitch={["video", "remix", "blackfx"].includes(section)} />
  );

  const Section = SECTIONS[section];
  if (!Section) return <Navigate to={`/studio/${projectId}/image`} replace />;

  return (
    <div className="h-screen flex flex-col relative z-[1] bg-transparent text-zinc-100 overflow-hidden">
      <TopNav />
      {!catalog.configured && section !== "blackfx" && (
        <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-sm flex items-center gap-2" data-testid="sume-key-banner">
          <KeyRound className="w-4 h-4" /> Generation is coming soon.
        </div>
      )}
      {catalog.error && <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/30 text-red-300 text-sm" data-testid="sume-error-banner">{brand(catalog.error)}</div>}
      <ErrorBoundary key={`eb-${projectId}-${section}`}>
      <Section
        key={`${projectId}-${section}-${resetN}`}
        modelPick={modelPick}
        projectId={projectId}
        models={catalog.models}
        configured={catalog.configured}
        items={items}
        references={references}
        onUploaded={onUploaded}
        generate={generate}
        onOpen={setLightbox}
        header={header}
        onChanged={loadHistory}
        onRetry={recreate}
        seed={seed && (seed.section || seed.category) === section ? seed : null}
        onReset={onReset}
      />
      </ErrorBoundary>
      <RecycleBin open={trashOpen} onOpenChange={setTrashOpen} projectId={projectId} onChanged={() => { loadHistory(); setTick((t) => t + 1); }} />
      <Lightbox open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)} item={lightbox} nav={lbNav}
        onRecreate={(g) => { setLightbox(null); recreate(g); }}
        onDeleted={() => { setLightbox(null); loadHistory(); }} onChanged={loadHistory} />
      <JobsDock jobs={history.filter((g) => g.status === "running")} onGo={(g) => nav(`/studio/${projectId}/${g.section || "image"}`)} />
    </div>
  );
}
